import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAllGrupos, descargarNotasGrupo } from '../../../services/grupo.service';

const DescargarNotas = () => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const debounceRef = useRef(null);

  const fetchGrupos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllGrupos();
      const normalized = data
        .filter(g => g?.idGrupo)
        .map(g => ({
          idGrupo: g.idGrupo,
          nombre: g.nombre || 'Grupo sin nombre',
          nivel: g.nivel?.nombre || 'Sin nivel',
          docente: g.docente
            ? `${g.docente.nombre || ''} ${g.docente.apellido || ''}`.trim()
            : 'Sin docente',
          modulo: g.modulo?.nombre || 'Sin módulo'
        }));
      setGrupos(normalized);
    } catch (err) {
      console.error(err);
      setError('Error al cargar los grupos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGrupos();
  }, [fetchGrupos]);

  // Debounce searchTerm → debouncedTerm
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  // Filtrado basado en debouncedTerm
  const filtered = grupos.filter(g => {
    const q = debouncedTerm.toLowerCase();
    return (
      g.nombre.toLowerCase().includes(q) ||
      g.nivel.toLowerCase().includes(q) ||
      g.docente.toLowerCase().includes(q) ||
      g.modulo.toLowerCase().includes(q)
    );
  });

  const allFilteredSelected = filtered.length > 0 && filtered.every(g => selectedGroups.some(s => s.idGrupo === g.idGrupo));

  const toggleGroup = useCallback(grupo => {
    setSelectedGroups(prev =>
      prev.some(x => x.idGrupo === grupo.idGrupo)
        ? prev.filter(x => x.idGrupo !== grupo.idGrupo)
        : [...prev, grupo]
    );
  }, []);

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedGroups(prev => prev.filter(x => !filtered.some(f => f.idGrupo === x.idGrupo)));
    } else {
      setSelectedGroups(prev => {
        const filteredIds = new Set(filtered.map(f => f.idGrupo));
        const newOnes = filtered.filter(f => !prev.some(p => p.idGrupo === f.idGrupo));
        return [...prev, ...newOnes];
      });
    }
  };

  const handleDownload = async () => {
    if (!selectedGroups.length) {
      setError('Selecciona al menos un grupo');
      return;
    }
    setDownloadLoading(true);
    setError(null);
    for (const g of selectedGroups) {
      try {
        await descargarNotasGrupo(g.idGrupo);
        await new Promise(r => setTimeout(r, 500));
      } catch {
        setError(prev => prev ? `${prev}. Error en ${g.nombre}` : `Error en ${g.nombre}`);
      }
    }
    setDownloadLoading(false);
  };

  useEffect(() => {
    if (!dropdownOpen) return;
    const onClickOutside = () => setDropdownOpen(false);
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, [dropdownOpen]);

  const clearAll = () => setSelectedGroups([]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600" />
        <p className="mt-4 text-base text-gray-600">Cargando lista de grupos...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-visible">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Descargar Planillas de Notas</h1>
            <p className="text-base text-blue-100 mt-1">Selecciona grupos y descarga Notas</p>
          </div>
          <span className="bg-blue-400 text-white px-3 py-1 rounded-full text-sm font-medium shadow-sm">
            {grupos.length} grupos
          </span>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg flex items-start shadow-sm">
              <svg className="flex-shrink-0 h-5 w-5 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div className="text-base">
                <p>{error}</p>
                <button onClick={() => setError(null)} className="mt-2 text-sm underline hover:text-red-900 font-medium">
                  Entendido
                </button>
              </div>
            </div>
          )}

          {/* Diseño elegante de dos columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Columna Izquierda: Lista de Grupos Disponibles */}
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Grupos Disponibles
                  </h2>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    {grupos.length} total
                  </span>
                </div>

                {/* Barra de búsqueda elegante */}
                <div className="relative mb-4">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar grupos por nombre, docente o módulo..."
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base shadow-sm transition-all"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Botón seleccionar todos */}
                <div className="flex items-center justify-between mb-4">
                  <label className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAll}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                    />
                    {allFilteredSelected ? 'Deseleccionar todos los filtrados' : 'Seleccionar todos los filtrados'}
                  </label>
                </div>

                {/* Lista de grupos con diseño elegante */}
                <div className="max-h-96 overflow-y-auto space-y-2 pr-2">
                  {filtered.length ? (
                    filtered.map(g => (
                      <div
                        key={g.idGrupo}
                        onClick={() => toggleGroup(g)}
                        className={`group p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          selectedGroups.some(x => x.idGrupo === g.idGrupo) 
                            ? 'border-blue-500 bg-blue-50 shadow-md' 
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`mt-1 h-5 w-5 border-2 rounded-full flex items-center justify-center transition-all ${
                              selectedGroups.some(x => x.idGrupo === g.idGrupo) 
                                ? 'bg-blue-600 border-blue-600' 
                                : 'border-gray-300 group-hover:border-blue-400'
                            }`}
                          >
                            {selectedGroups.some(x => x.idGrupo === g.idGrupo) && (
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-800 truncate">{g.nombre}</h3>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className="px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                {g.modulo}
                              </span>
                              <span className="px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                                {g.nivel}
                              </span>
                            </div>
                            {g.docente !== 'Sin docente' && (
                              <p className="text-sm text-gray-600 mt-1 truncate">
                                👨‍🏫 {g.docente}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 6.709A7.962 7.962 0 0112 5c-2.34 0-4.29 1.009-5.824 2.709"/>
                      </svg>
                      <p className="text-base">
                        {debouncedTerm ? 'No se encontraron grupos' : 'No hay grupos disponibles'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Columna Derecha: Grupos Seleccionados + Botón de Descarga */}
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Grupos Seleccionados
                  </h2>
                  {selectedGroups.length > 0 && (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {selectedGroups.length} seleccionado{selectedGroups.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {selectedGroups.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    {selectedGroups.map(g => (
                      <div
                        key={g.idGrupo}
                        className="flex items-center justify-between bg-white rounded-lg border border-green-200 p-3 shadow-sm"
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-gray-800 truncate">{g.nombre}</h3>
                          <div className="flex flex-wrap gap-1 mt-1">
                            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {g.modulo}
                            </span>
                            <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                              {g.nivel}
                            </span>
                          </div>
                        </div>
                        <button 
                          onClick={() => toggleGroup(g)} 
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                    </svg>
                    <p className="text-base font-medium">Ningún grupo seleccionado</p>
                    <p className="text-sm mt-1">Selecciona grupos de la columna izquierda</p>
                  </div>
                )}

                {selectedGroups.length > 0 && (
                  <button 
                    onClick={clearAll} 
                    className="w-full mt-4 text-sm text-red-600 hover:text-red-800 font-medium py-2 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    🗑️ Limpiar selección
                  </button>
                )}
              </div>

              {/* Botón de descarga elegante */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <button
                  onClick={handleDownload}
                  disabled={downloadLoading || !selectedGroups.length}
                  className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center transition-all duration-300 ${
                    downloadLoading
                      ? 'bg-blue-400 text-white cursor-wait shadow-lg'
                      : !selectedGroups.length
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-dashed border-gray-300'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl transform hover:-translate-y-1'
                  }`}
                >
                  {downloadLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generando archivos...
                    </>
                  ) : !selectedGroups.length ? (
                    <>
                      <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Selecciona grupos para descargar
                    </>
                  ) : selectedGroups.length > 1 ? (
                    <>
                      <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      📥 Descargar {selectedGroups.length} Notas
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      📥 Descargar Notas
                    </>
                  )}
                </button>
                
                {selectedGroups.length > 0 && (
                  <p className="text-center text-sm text-green-600 mt-3 font-medium">
                    ✅ {selectedGroups.length} grupo{selectedGroups.length !== 1 ? 's' : ''} listo{selectedGroups.length !== 1 ? 's' : ''} para descargar
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DescargarNotas;